import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import axios from 'axios';
import { useSource } from '../../../util/SourceContext';

interface FormProps {
  preselectService?: string;
}

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

interface FormData {
  source: string;
  otherSource: string;
  name: string;
  number: string;
  address: string;
  size: string;
  date: string;
  service: string;
  baseboards: boolean;
  fridgeInside: boolean;
  ovenInside: boolean;
  carpetShampooRooms: number;
  /** User must check; initial state is always false (never pre-checked). */
  termsAccepted: boolean;
  smsConsent: boolean;
  honeypot: string;  // Honeypot field added here
}

const libraries: ('places')[] = ['places'];

const getApiBaseUrl = () =>
  process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const isDevelopment = process.env.NODE_ENV === 'development';

const Form: React.FC<FormProps> = ({ preselectService }) => {
  const sourceFromContext = useSource();
  const [formData, setFormData] = useState<FormData>({
    source: '',
    otherSource: '',
    name: '',
    number: '',
    address: '',
    size: '',
    date: '',
    service: '',
    baseboards: false,
    fridgeInside: false,
    ovenInside: false,
    carpetShampooRooms: 0,
    termsAccepted: false,
    smsConsent: false,
    honeypot: '',  // Honeypot field added here
  });

  const [isAddressSelected, setIsAddressSelected] = useState(false); // Track if address is selected from autocomplete
  const [isOtherSource, setOtherSource] = useState(false); // Track if address is selected from autocomplete
  const [isAddressValid, setIsAddressValid] = useState(true); // Track if the address is valid
  const [submitted, setSubmitted] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false); // Show what's missing after submit click
  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [couponMessage, setCouponMessage] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [showCouponConfirmModal, setShowCouponConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  //const [totalPrice, setTotalPrice] = useState(0);

  const today = new Date();
  const twoYearsLater = new Date(today.getFullYear() + 2, today.getMonth(), today.getDate());

  const formattedToday = today.toISOString().split('T')[0]; // Format to YYYY-MM-DD
  const formattedTwoYearsLater = twoYearsLater.toISOString().split('T')[0]; // Format to YYYY-MM-DD



  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries,
    ...(isDevelopment ? { skip: true } : {}),
  });

  useEffect(() => {
    if (preselectService) {
      setFormData((prev) => ({ ...prev, service: preselectService }));
    }
  }, [preselectService]);

  // Auto-select "Where did you hear about us?" from URL (?source=...) stored in session
  useEffect(() => {
    if (sourceFromContext && sourceFromContext !== 'default') {
      setFormData((prev) => ({ ...prev, source: sourceFromContext }));
      setOtherSource(sourceFromContext === 'Other');
    }
  }, [sourceFromContext]);

  useEffect(() => {
    const handleTouchStart = () => {
      if (autocompleteRef.current && !isAddressSelected) {
        autocompleteRef.current.set("place", null);
      }
    };
    document.addEventListener("touchstart", handleTouchStart);
    return () => document.removeEventListener("touchstart", handleTouchStart);
  }, [isAddressSelected]); // Only run this effect if address is not selected
  
  const handlePlaceSelect = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      const formattedAddress = place?.formatted_address || '';
      setFormData((prev) => ({
        ...prev,
        address: formattedAddress,
      }));
      setIsAddressSelected(!!formattedAddress); // Mark address as selected
      setIsAddressValid(!!formattedAddress);    // Reset any validation errors
    }
  };
  
  
  const handleBlur = () => {
    // On blur, we manually reset the autocomplete's input box to force it to close suggestions.
    // On blur, if the address was not selected from autocomplete, mark it invalid
    if (!isAddressSelected) {
      setIsAddressValid(false);
    }
    
    if (autocompleteRef.current) {
      const input = autocompleteRef.current.getPlace();
      if (!input) {
        autocompleteRef.current.set('place', null);
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else if (type === 'number') {
      setFormData((prev) => ({
        ...prev,
        [name]: parseInt(value, 10),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (name === 'address') {
      setIsAddressSelected(false); // If user starts typing again, mark the address as not selected
      setIsAddressValid(true); // Reset validation
    }
  };

  const isAddressAcceptable = isDevelopment
    ? !!formData.address?.trim()
    : isAddressSelected;

  const isFormValid = () => {
    return (
      formData.name &&
      formData.number &&
      formData.address &&
      formData.size &&
      formData.date &&
      formData.service &&
      formData.termsAccepted &&
      isAddressAcceptable &&
      formData.honeypot === '' // Honeypot field should be empty
    );
  };

  /** Returns a list of human-readable missing requirements for the form. */
  const getMissingFields = (): string[] => {
    const missing: string[] = [];
    if (!formData.name?.trim()) missing.push('Name');
    if (!formData.number?.trim()) missing.push('Phone Number');
    if (!formData.address?.trim()) missing.push('Address');
    else if (!isAddressAcceptable) missing.push(isDevelopment ? 'Address' : 'Address (select from suggestions)');
    if (!formData.size) missing.push('Size (square feet)');
    if (!formData.date) missing.push('Preferable date');
    if (!formData.service) missing.push('Service type');
    if (!formData.source) missing.push('Where did you hear about us?');
    if (formData.source === 'Other' && !formData.otherSource?.trim()) missing.push('Please specify where you heard about us');
    if (!formData.termsAccepted) missing.push('Agreement to Terms and Conditions');
    if (formData.honeypot !== '') return missing; // don't list honeypot to user
    return missing;
  };

  const handleValidateCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      setCouponStatus('idle');
      setCouponMessage('');
      return;
    }
    setValidatingCoupon(true);
    setCouponStatus('idle');
    setCouponMessage('');
    try {
      const { data } = await axios.post<{ valid: boolean; message: string }>(
        `${getApiBaseUrl()}/api/coupons/validate`,
        { name: code }
      );
      setCouponStatus(data.valid ? 'valid' : 'invalid');
      setCouponMessage(data.message ?? (data.valid ? 'Coupon is valid. You can submit the form.' : 'This coupon is expired or invalid.'));
    } catch {
      setCouponStatus('invalid');
      setCouponMessage('Unable to validate coupon. Please try again.');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleWhereFromChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleChange(e);
    
    if (e.target.value === 'Other') {
      setFormData((prev) => ({
        ...prev,
        source: 'Other',
      }));
      setOtherSource(true);
    } else {
      setFormData((prev) => ({
        ...prev,
        source: e.target.value,
      }));
      if (isOtherSource) {
        setOtherSource(false);
        setFormData((prev) => ({
          ...prev,
          otherSource: '',
        }));
      }
    }
  }


  const submitQuote = async (includeCoupon: boolean) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload = {
        formData: {
          ...formData,
          termsAccepted: formData.termsAccepted === true,
          smsConsent: formData.smsConsent === true,
          ...(includeCoupon && couponStatus === 'valid' && couponCode.trim() ? { couponName: couponCode.trim() } : {}),
        },
      };
      await axios.post(
        `${getApiBaseUrl()}/api/submitQuote`,
        payload
      );
      setSubmitted(true);
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-11414285835/PftvCJKp18MZEIvc4MIq',
        });
      }
    } catch (err: any) {
      console.error('Failed to submit quote', err);
      const msg = err?.response?.data?.message;
      if (msg && typeof msg === 'string' && (msg.includes('coupon') || msg.includes('expired') || msg.includes('invalid'))) {
        setCouponStatus('invalid');
        setCouponMessage(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);

    if (!isFormValid()) {
      return;
    }

    const hasCouponCodeButNotApplied = couponCode.trim() && couponStatus !== 'valid';
    if (hasCouponCodeButNotApplied) {
      setShowCouponConfirmModal(true);
      return;
    }

    await submitQuote(true);
  };

  const handleServiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleChange(e);
    if (e.target.value === 'D' || e.target.value === 'M') {
      setFormData((prev) => ({
        ...prev,
        fridgeInside: true,
        ovenInside: true,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        fridgeInside: false,
        ovenInside: false,
      }));
    }
  };

  if (!isDevelopment && !isLoaded) return <div>Loading...</div>;
  
  return (
    <div className="flex items-start justify-center bg-white py-10">
      {showCouponConfirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="coupon-confirm-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 id="coupon-confirm-title" className="text-lg font-semibold text-gray-800 mb-3">
              Coupon not applied
            </h2>
            <p className="text-gray-600 mb-6">
              You've entered a coupon code but haven't applied it. Submit without using this coupon?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowCouponConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={async () => {
                  setShowCouponConfirmModal(false);
                  await submitQuote(false);
                }}
                className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting…' : 'Submit without coupon'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="relative max-w-3xl w-full bg-white rounded-lg p-8 shadow-2xl mx-4">
        <header className="text-xl font-semibold text-gray-800">Online Quote</header>
  
        {!submitted ? (
          <form onSubmit={handleSubmit} className="relative mt-4">
            <div className="mt-8">
              <span className="block mb-2 text-lg font-medium text-gray-800">Personal Details</span>
              <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-1/3 px-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700" htmlFor="name">
                    Name:
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter First and Last name"
                    maxLength={50}  // Limit the input to 30 characters
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
  
                <div className="w-full md:w-1/3 px-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700" htmlFor="number">
                    Phone Number:
                  </label>
                  <input
                    type="text"
                    id="number"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                    required
                    placeholder="7255774523"
                    pattern="^[0-9]*$"
                    maxLength={10}
                    minLength={10}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>

                <div className="w-full px-2 mb-4">
                  <p className="text-xs text-gray-500 mb-1">SMS notifications (optional)</p>
                  <div className="sms-consent text-sm text-gray-800">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        id="smsConsent"
                        name="smsConsent"
                        checked={formData.smsConsent === true}
                        onChange={handleChange}
                        className="mt-1 shrink-0"
                      />
                      <span>
                        I agree to receive SMS messages from Evidence Cleaning regarding appointment confirmations, scheduling, service updates, customer support, and related service messages.
                      </span>
                    </label>
                    <p className="sms-disclosure text-gray-600 mt-2 text-sm">
                      Message frequency varies.
                      <br />
                      Message and data rates may apply.
                      <br />
                      Reply STOP to unsubscribe.
                      <br />
                      Reply HELP for assistance.
                    </p>
                    <p className="sms-links mt-1 text-sm">
                      <Link
                        to="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        Terms and Conditions
                      </Link>
                      {' | '}
                      <Link
                        to="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        Privacy Policy
                      </Link>
                    </p>
                  </div>
                </div>

                {/* Honeypot field: Hidden from users, but might get filled by bots */}
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleChange}
                  style={{ display: 'none' }} // Hide this field
                />

                <div className="w-full md:w-1/3 px-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700" htmlFor="address">
                    Address:
                  </label>
                  {isDevelopment ? (
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Enter your address"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    />
                  ) : (
                    <Autocomplete
                      onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
                      onPlaceChanged={handlePlaceSelect}
                    >
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter your address"
                        required
                        className={`mt-1 block w-full border ${
                          isAddressValid
                            ? 'border-gray-300'
                            : 'border-red-500'
                        } rounded-md shadow-sm p-2`}
                      />
                    </Autocomplete>
                  )}
                  {!isDevelopment && !isAddressValid && (
                    <p className="text-red-500 text-sm mt-1">
                      Please select a valid address from the autocomplete suggestions.
                    </p>
                  )}
                </div>
  
                <div className="w-full md:w-1/3 px-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700" htmlFor="size">
                    Size:
                  </label>
                  <select
                    id="size"
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value="" disabled>
                      Size in square feet
                    </option>
                    <option value="0">0 - 1000sqft</option>
                    <option value="1">1001 - 1500sqft</option>
                    <option value="2">1501 - 2000sqft</option>
                    <option value="3">2001 - 2500sqft</option>
                    <option value="4">2501 - 3000sqft</option>
                    <option value="5">3001 - 3500sqft</option>
                    <option value="6">3501 - 4000sqft</option>
                    <option value="7">4001 - 4500sqft</option>
                    <option value="8">4501 - 5000sqft</option>
                    <option value="9">5001 - 5500sqft</option>
                    <option value="10">5501 - 6000sqft</option>
                  </select>
                </div>
  
                <div className="w-full md:w-1/3 px-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700" htmlFor="date">
                    Preferable date:
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    min={formattedToday}  // Minimum date is today
                    max={formattedTwoYearsLater}  // Maximum date is two years from today
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
  
                <div className="w-full md:w-1/3 px-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700" htmlFor="service">
                    Service:
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleServiceChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value="" disabled>
                      Type of Service
                    </option>
                    <option value="S">Standard Cleaning</option>
                    <option value="D">Deep Cleaning</option>
                    <option value="M">Move in/out</option>
                  </select>
                </div>
              </div>
            </div>
  
            <div className="mt-8">
              <span className="block mb-2 text-lg font-medium text-gray-800">Select Extras:</span>
              <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-1/3 px-2 mb-4 flex items-center">
                  <img src="https://media.istockphoto.com/id/1307239557/vector/floor-transition-moulding-vector-icon-design-editable-line-stroke.jpg?s=612x612&w=0&k=20&c=P24Y7VggU4xYVw_WPrZ-qaZtvN6TH8G1A3dfcTE01vI=" alt="Baseboards.png" className="w-12 h-12 mr-2" />
                  <label className="mr-2" htmlFor="baseboards">Baseboards</label>
                  <input
                    type="checkbox"
                    id="baseboards"
                    name="baseboards"
                    checked={formData.baseboards}
                    onChange={handleChange}
                    className="ml-2"
                  />
                </div>
                <div className="w-full md:w-1/3 px-2 mb-4 flex items-center">
                  <img src="https://cdn-icons-png.flaticon.com/512/1985/1985123.png" alt="Fridge.png" className="w-12 h-12 mr-2" />
                  <label className="mr-2" htmlFor="fridgeInside">Fridge Inside</label>
                  <input
                    type="checkbox"
                    id="fridgeInside"
                    name="fridgeInside"
                    checked={formData.fridgeInside}
                    onChange={handleChange}
                    className="ml-2"
                    disabled={formData.service === 'D' || formData.service === 'M'}
                  />
                </div>
                <div className="w-full md:w-1/3 px-2 mb-4 flex items-center">
                  <img src="https://cdn-icons-png.flaticon.com/512/1941/1941808.png" alt="Oven.png" className="w-12 h-12 mr-2" />
                  <label className="mr-2" htmlFor="ovenInside">Oven Inside</label>
                  <input
                    type="checkbox"
                    id="ovenInside"
                    name="ovenInside"
                    checked={formData.ovenInside}
                    onChange={handleChange}
                    className="ml-2"
                    disabled={formData.service === 'D' || formData.service === 'M'}
                  />
                </div>
                {/* <div className="w-full px-2 mb-4 flex items-center">
                  <img src="https://cdn-icons-png.flaticon.com/512/11058/11058836.png" alt="Carpet.png" className="h-12 mr-2" />
                  <label className="mr-2" htmlFor="carpetShampooRooms">Carpet Shampoo (Rooms)</label>
                  <input
                    type="number"
                    id="carpetShampooRooms"
                    name="carpetShampooRooms"
                    value={formData.carpetShampooRooms}
                    onChange={handleChange}
                    min="0"
                    className="ml-4 mt-1 block w-[70px] border border-gray-300 rounded-md shadow-sm p-2 text-center"
                  />
                </div> */}
              </div>

              <div>
                <div className="w-full md:w-1/3 px-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700" htmlFor="service">
                    Where did you hear about us?:
                  </label>
                  <select
                    id="source"
                    name="source"
                    value={formData.source}
                    onChange={handleWhereFromChange} // Call this to handle selection change
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value="" disabled>
                      Select one
                    </option>
                    <option value="Yelp">Yelp</option>
                    <option value="Google">Google</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Facebook">Facebook</option>
                    <option value="TikTok">TikTok</option>
                    <option value="Friends/Family">Friends/Family</option>
                    <option value="Other">Other</option> {/* "Other" option */}
                  </select>
                </div>

                {isOtherSource && ( // Conditionally render the text box
                  <div className="w-full md:w-1/3 px-2 mb-4">
                    <label className="block text-sm font-medium text-gray-700" htmlFor="otherSource">
                      Please specify:
                    </label>
                    <input
                      type="text"
                      id="otherSource"
                      name="otherSource"
                      value={formData.otherSource || ''} // Optional chaining for "otherService"
                      onChange={handleChange}
                      maxLength={50} // Limit the input to 50 characters
                      placeholder="Specify other places"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    />
                  </div>
                )}
              </div>

              <div className="w-full md:w-1/3 px-2 mb-4">
                <label className="block text-sm font-medium text-gray-700" htmlFor="couponCode">
                  Coupon code <span className="text-gray-500 font-normal">(optional)</span>:
                </label>
                <div className={`flex flex-wrap items-center gap-2 mt-1 rounded-md transition-colors ${couponStatus === 'valid' ? 'ring-2 ring-green-500 ring-offset-1 bg-green-50/50 p-1 -m-1' : ''}`}>
                  <input
                    type="text"
                    id="couponCode"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value);
                      setCouponStatus('idle');
                      setCouponMessage('');
                    }}
                    placeholder="Enter coupon code"
                    className={`block flex-1 min-w-[140px] rounded-md shadow-sm p-2 border ${
                      couponStatus === 'valid'
                        ? 'border-green-500 bg-white'
                        : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={handleValidateCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                    className="px-3 py-2 bg-gray-700 text-white text-sm rounded-md shadow-sm hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {validatingCoupon ? 'Checking…' : 'Apply'}
                  </button>
                  {couponStatus === 'valid' && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-green-600 text-white" aria-live="polite">
                      <span aria-hidden>✓</span> Applied
                    </span>
                  )}
                </div>
                {couponMessage && (
                  <p className={`mt-1 text-sm ${couponStatus === 'valid' ? 'text-green-600 font-medium' : couponStatus === 'invalid' ? 'text-red-600' : 'text-gray-600'}`}>
                    {couponMessage}
                  </p>
                )}
              </div>


              <p className="text-sm text-gray-600 mt-2">
                Note: Stairways count as a room to be carpet washed.<br />
                Note: Service area within 20 miles of Las Vegas, NV.
              </p>
            </div>

            <div className="mt-8 flex items-start">
              <input
                type="checkbox"
                id="termsAccepted"
                name="termsAccepted"
                checked={formData.termsAccepted === true}
                onChange={handleChange}
                required
                autoComplete="off"
                className="mr-2 mt-1 shrink-0"
              />
              <label htmlFor="termsAccepted" className="text-sm">
                I agree to the{' '}
                <Link to="/terms" className="text-blue-500 underline">
                  Terms and Conditions
                </Link>
                .
              </label>
            </div>

            {submitAttempted && !isFormValid() && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md" role="alert">
                <p className="font-medium text-amber-800">Please complete the following:</p>
                <ul className="mt-2 list-disc list-inside text-amber-800 text-sm">
                  {getMissingFields().map((field) => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-8 w-full md:w-auto px-4 py-2 bg-gray-800 text-white rounded-md shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </form>
        ) : (
          <div>
            <h2 className="text-2xl font-bold">Thank you!</h2>
            <p>
              Your quote request has been submitted, 
            </p>
            <p>We will contact you shortly to finish your appointment and take care of any additional requests.</p>
            <button
              onClick={() => setSubmitted(false)}
              className="mt-4 w-full md:w-auto px-4 py-2 bg-gray-800 text-white rounded-md shadow-md"
            >
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  )
};

export default Form;